import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { refreshAccessToken } from "../lib/authRefresh";

// Always use current origin - Kubernetes ingress routes /api to backend
const API = window.location.origin;
const AuthContext = createContext(null);

// Global axios interceptor: 401 alinca access token'i BIR KEZ yenile (single-flight) ve isteği tekrarla.
// Boylece 24 saatlik access token suresi dolunca kullanici atilmaz; sessizce yenilenir.
let _interceptorReady = false;
function ensureAuthInterceptor() {
  if (_interceptorReady) return;
  _interceptorReady = true;
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config || {};
      const status = error.response?.status;
      const url = original.url || "";
      if (status === 401 && !original._retry && !url.includes("/auth/refresh") && !url.includes("/auth/login")) {
        original._retry = true;
        const newTok = await refreshAccessToken();
        if (newTok) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newTok}`;
          return axios(original);
        }
      }
      return Promise.reject(error);
    }
  );
}
ensureAuthInterceptor();

function getStoredToken() {
  return localStorage.getItem("auth_token");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // Retry up to 2 times on network error
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        setUser(data);
        setLoading(false);
        return;
      } catch (e) {
        if (e.code === "ERR_NETWORK" || e.code === "ECONNABORTED") {
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
            continue;
          }
        }
        localStorage.removeItem("auth_token");
        setUser(null);
        break;
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/api/auth/login`,
      { email, password },
      { timeout: 10000 }
    );
    if (data.token) {
      localStorage.setItem("auth_token", data.token);
    }
    setUser({ email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      const token = getStoredToken();
      await axios.post(`${API}/api/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 3000,
      });
    } catch {}
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  const role = (user?.role || "").toLowerCase();
  const isSuperAdmin = role === "admin" || role === "super_admin";
  const isManager = role === "manager";
  const isGarson = !!user && !isSuperAdmin && !isManager; // staff/garson
  const canManage = isSuperAdmin || isManager;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, role, isSuperAdmin, isManager, isGarson, canManage }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
